import { EditorTemplateStatus, EditorTemplateVersion } from '@dev/template-editor';
import { cloneDeep, forEach, has, isArray, isEmpty } from 'lodash-es';

type Block = {
    attributes?: {
        content?: string;
        [key: string]: unknown;
    };
    innerBlocks?: Block[];
    [key: string]: unknown;
};

type StyleConfig = {
    style: string;
    className: string;
};

const defaultButtonStyles = {
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
} as const;

const convertFormattingTagsToStyledSpans = (htmlContent: string, tagStyles: Map<string, StyleConfig>) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const createStyledSpan = (config: StyleConfig) => {
        const span = document.createElement('span');
        span.style.cssText = config.style;
        span.classList.add(config.className);
        return span;
    };

    const isTextNode = (node: Element) => node.nodeType === Node.TEXT_NODE;
    const isElementNode = (node: Element) => node.nodeType === Node.ELEMENT_NODE;

    const processNode = (node: Element) => {
        if (isTextNode(node)) {
            return node.cloneNode(false);
        }

        if (isElementNode(node)) {
            const tagName = node.tagName.toLowerCase();
            if (tagStyles.has(tagName)) {
                return createStyledSpan(tagStyles.get(tagName));
            }
            return node.cloneNode(false);
        }

        return node.cloneNode(false);
    };

    const applyStylesToAnchors = (node: HTMLElement) => {
        if (!(node instanceof Element)) return;
        const anchors = node.querySelectorAll('a:not([role])');
        anchors.forEach((anchor: HTMLAnchorElement) => {
            anchor.style.display = 'inline-block';
        });
    };

    const recursivelyStyleContent = (node: Element) => {
        const currentNode = processNode(node);

        node.childNodes.forEach((child: Element) => {
            currentNode.appendChild(recursivelyStyleContent(child));
        });

        if (node === doc.body || doc.body.contains(node)) {
            applyStylesToAnchors(currentNode as HTMLElement);
        }

        return currentNode;
    };

    const styledBody = recursivelyStyleContent(doc.body);

    return (styledBody as HTMLElement).innerHTML;
};

const convertLinksColorToObject = (styles: EditorTemplateVersion['settings']['styles'] & { linksColor?: string }) => {
    if (!has(styles, 'linksColor')) return styles;
    const linkColor = styles.linksColor;
    const { linksColor, ...newStyles } = styles;
    const updatedStyles = { ...newStyles, links: { color: linkColor } };
    forEach(defaultButtonStyles, (value, key) => (updatedStyles.links[key] = value));
    return updatedStyles;
};

const updateButtonStyles = (buttonStyles: EditorTemplateVersion['settings']['styles']['button']) => {
    const allKeysExist = Object.keys(defaultButtonStyles).every((key) => has(buttonStyles, key));
    if (allKeysExist) return buttonStyles;

    const updatedStyles = { ...buttonStyles };

    forEach(defaultButtonStyles, (value, key) => (updatedStyles[key] = value));

    return updatedStyles;
};

const updateStylesInTemplate = (templateJson: EditorTemplateVersion): EditorTemplateVersion => {
    const updatedTemplateJson = cloneDeep(templateJson);

    if (!isEmpty(updatedTemplateJson?.settings?.styles)) {
        updatedTemplateJson.settings.styles = convertLinksColorToObject(updatedTemplateJson.settings.styles);
    }

    if (!isEmpty(updatedTemplateJson?.settings?.styles.button)) {
        updatedTemplateJson.settings.styles.button = updateButtonStyles(updatedTemplateJson.settings.styles.button);
    }

    if (!has(updatedTemplateJson?.settings?.styles, 'list')) {
        updatedTemplateJson.settings.styles.list = { ...updatedTemplateJson?.settings?.styles?.paragraph };
    }

    return updatedTemplateJson;
};

const processBlock = (block: Block, tagStyles: Map<string, StyleConfig>) => {
    if (block?.attributes?.content) {
        block.attributes.content = convertFormattingTagsToStyledSpans(block?.attributes?.content, tagStyles);
    }

    if (!isEmpty(block?.innerBlocks)) {
        forEach(block?.innerBlocks, (block) => processBlock(block, tagStyles));
    }
};

export const updateTemplateContent = (
    templateLoadedJson: EditorTemplateVersion,
    status: EditorTemplateStatus,
    tagStyles: Map<string, StyleConfig>
): EditorTemplateVersion => {
    try {
        if (
            !templateLoadedJson ||
            (isArray(templateLoadedJson?.blocks) && status !== EditorTemplateStatus.LOADED) ||
            !has(templateLoadedJson?.settings?.styles, 'linksColor') //  linksColor does not exists in new templates
        ) {
            return templateLoadedJson;
        }

        const updatedTemplateJson = updateStylesInTemplate(templateLoadedJson);
        forEach(updatedTemplateJson?.blocks, (block) => processBlock(block, tagStyles));

        return updatedTemplateJson;
    } catch (error) {
        console.error(error);
        return templateLoadedJson;
    }
};
